import { useTranslation } from 'react-i18next';
import { formatIsoDateWithTime } from '../../common/Dates';
import { useDataContext } from '../../contexts/DataContext';
import './styles/PatientInfo.scss';

type Props = {
    code: string;
    sex: string;
    age: number;
    cdr: number;
    sob: number;
    mmse: number | null;
    apoe4: string;
    pet: string;
    categoryId: string | null;
    categoryEs: string | null;
    categoryEn: string | null;
    categoryCa: string | null;
    interpretationEs: string | null;
    interpretationEn: string | null;
    interpretationCa: string | null;
    username: string | null;
    userCode: string | null;
    probCtrl: number | null;
    probMci: number | null;
    probAd: number | null;
    created: string | null;
    neuroDate: string | null;
};

const PatientInfo = ({
    code,
    sex,
    age,
    cdr,
    sob,
    mmse,
    apoe4,
    pet,
    categoryId,
    categoryEs,
    categoryEn,
    categoryCa,
    interpretationEs,
    interpretationEn,
    interpretationCa,
    username,
    userCode,
    probCtrl,
    probMci,
    probAd,
    created,
    neuroDate,
}: Props) => {
    const { t } = useTranslation();
    const { softwareVersionProp } = useDataContext();
    const { i18n } = useTranslation();
    const { reportIssuedes, reportIssueden } = formatIsoDateWithTime(neuroDate);

    // Función para formatear texto con MAP-AD®
    const formatMapAdText = (text: string) => {
        if (!text) return text;

        // Dividir el texto por "MAP-AD" manteniendo espacios
        const regex = /(MAP-AD®?\s*)/g;
        const parts = text.split(regex);

        if (parts.length === 1) return text; // No contiene MAP-AD

        return (
            <>
                {parts.map((part, index) => {
                    if (part.match(regex)) {
                        // Esta parte contiene MAP-AD, reemplazarla con formato
                        const hasSpace = part.includes(' ');
                        return (
                            <span key={index}>
                                MAP-AD<sup>®</sup>
                                {hasSpace ? ' ' : ''}
                            </span>
                        );
                    }
                    return <span key={index}>{part}</span>;
                })}
            </>
        );
    };

    return (
        <div className="page">
            <div className="header">
                <div className="logo">
                    <img
                        src={process.env.PUBLIC_URL + '/logo.png'}
                        alt="Logo Admit"
                        className="logo"
                    />
                </div>
                <div className="company-data">
                    {t('patient_report.company_name')}
                    <br />
                    {t('patient_report.address_line1')}
                    <br />
                    {t('patient_report.address_line2')}
                    <br />
                    {t('patient_report.phone')}
                    <br />
                    {t('patient_report.email')}
                    <br />
                </div>
            </div>

            <section className="general-data">
                <div className="patient-data">
                    <div className="title">{t('patient_report.patient_information')}</div>
                    <div className="data">
                        <div className="patient-id">
                            <div className="title">{t('patient_report.patient_id')}</div>
                            <div className="data">{code}</div>
                        </div>
                        <div className="age">
                            <div className="title">{t('patient_report.age')}</div>
                            <div className="data">{age}</div>
                        </div>
                        <div className="CDR">
                            <div className="title">{t('patient_report.cdr')}</div>
                            <div className="data">{cdr}</div>
                        </div>
                        <div className="CDR-SOB">
                            <div className="title">{t('patient_report.sob')}</div>
                            <div className="data">{sob}</div>
                        </div>
                        <div className="APOE">
                            <div className="title">{t('patient_report.apoe')}</div>
                            <div className="data">{apoe4}</div>
                        </div>
                    </div>
                </div>
                <div className="user-data">
                    <div className="title">{t('patient_report.user_information')}</div>
                    <div className="data">
                        <div className="name">
                            <div className="title">{t('patient_report.user')}</div>
                            <div className="data">{username || t('label.not_available')}</div>
                        </div>
                        <div className="account">
                            <div className="title">{t('patient_report.account')}</div>
                            <div className="data">{userCode || t('label.not_available')}</div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="report-result">
                <div className="title">
                    MAP-AD<sup>®</sup> Test – {t('patient_report.test_report_title')}
                </div>
                <div className="result-data">
                    <div className="test-result">
                        <div className="title">{t('patient_report.test_result')}</div>
                        <div className="data">
                            <div className="progressed">
                                <div className="title">{t('patient_report.progressed_score')}</div>
                                <div className="data">
                                    {probAd !== null ? `${(probAd * 100).toFixed(1)}%` : '--'}
                                </div>
                            </div>
                            <div className="non-progressed">
                                <div className="title">
                                    {t('patient_report.non_progressed_score')}
                                </div>
                                <div className="data">
                                    {probMci !== null ? `${(probMci * 100).toFixed(1)}%` : '--'}
                                </div>
                            </div>
                            <div className="congnitively-normal">
                                <div className="title">
                                    {t('patient_report.cognitively_normal_score')}
                                </div>
                                <div className="data">
                                    {probCtrl !== null ? `${(probCtrl * 100).toFixed(1)}%` : '--'}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="category">
                        <div className="title">{t('patient_report.category')}</div>
                        <div className="data">
                            {i18n.language === 'es'
                                ? categoryEs
                                : i18n.language === 'en'
                                ? categoryEn
                                : categoryCa}
                        </div>
                    </div>
                    <div className="interpretation">
                        <div className="title">{t('patient_report.interpretation')}</div>
                        <div className="data">
                            <div className="interpretation-result">
                                {formatMapAdText(
                                    i18n.language === 'es'
                                        ? interpretationEs || ''
                                        : i18n.language === 'en'
                                        ? interpretationEn || ''
                                        : interpretationCa || ''
                                )}
                            </div>
                            <div className="interpretation-explanation">
                                {t('patient_report.interpretation_explanation')}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="clinical-importance">
                <div className="title">{t('patient_report.clinical_significance')}</div>
                <div className="data">
                    {formatMapAdText(t('patient_report.clinical_significance_body1'))}
                    <br />
                    {t('patient_report.clinical_significance_body2')}
                </div>
            </section>

            <section className="metodology-description">
                <div className="title">{t('patient_report.methodology')}</div>
                <div className="data">
                    {formatMapAdText(t('patient_report.methodology_body1'))}
                    {softwareVersionProp}
                    {formatMapAdText(t('patient_report.methodology_body2'))}
                </div>
            </section>

            <section className="legal-advice">
                <div className="title">{t('patient_report.legal_note')}</div>
                <div className="data">{t('patient_report.legal_note_body')}</div>
            </section>

            <footer className="patient-info-footer report">
                <div className="pdf-version">
                    MAP-AD<sup>®</sup> {t('patient_report.pdf_version')}
                </div>
                <div className="report-version">
                    <div className="report-date">
                        <div className="title">{t('patient_report.report_date_title')}</div>
                        <div className="data">
                            {i18n.language === 'en' ? reportIssueden : reportIssuedes}
                        </div>
                    </div>
                    <div className="patient-id">
                        <div className="title">{t('patient_report.patient_id')}</div>
                        <div className="data">{code}</div>
                    </div>
                </div>
                <div className="page-footer">{t('patient_report.page_count')}</div>
            </footer>

            <div className="watermark">
                <img
                    src={process.env.PUBLIC_URL + '/logo.png'}
                    alt="Logo Admit"
                    className="logo"
                />
            </div>
        </div>
    );
};

export default PatientInfo;
